import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import ChangePasswordModal from '../../components/modals/ChangePasswordModal';

interface EmployeeLoginForm {
  email: string;
  password: string;
}

interface EmployeeLoginProps {
  setIsAuthenticated: (auth: boolean) => void;
}

const EmployeeLogin: React.FC<EmployeeLoginProps> = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempSession, setTempSession] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmployeeLoginForm>();

  const onSubmit: SubmitHandler<EmployeeLoginForm> = async (data) => {
    try {
      setIsLoading(true);
      const response = await api.post(API_URLS.EMPLOYEE_LOGIN, {
        email: data.email,
        password: data.password,
      });

      if (response.data.success) {
        const { user, token, requiresPasswordChange } = response.data.data;

        if (requiresPasswordChange) {
          // Store temporary session for password change
          setTempSession({ user, token });
          setShowPasswordModal(true);
        } else {
          // Normal login flow
          const session = {
            token,
            user,
            isAuthenticated: true,
          };

          sessionManager.saveSession(session);
          setIsAuthenticated(true);

          // Show role-specific welcome message
          const userRole = user.role?.label || 'Team Member';
          showSuccessMsg(`Welcome back, ${userRole}! Login successful.`);
          navigate('/dashboard');
        }
      } else {
        showErrorMsg(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Employee login error:', error);
      showErrorMsg(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    if (tempSession) {
      const session = {
        token: tempSession.token,
        user: tempSession.user,
        isAuthenticated: true,
      };

      sessionManager.saveSession(session);
      setIsAuthenticated(true);
      setShowPasswordModal(false);

      // Show role-specific welcome message
      const userRole = tempSession.user.role?.label || 'Team Member';
      showSuccessMsg(`Password changed successfully! Welcome to STATDOG, ${userRole}.`);

      setTempSession(null);
      navigate('/dashboard');
    }
  };

  const handlePasswordChangeCancel = () => {
    setShowPasswordModal(false);
    setTempSession(null);
    reset();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-48 w-48 flex items-center justify-center">
            <img src="/images/logo-stacked.png" alt="STATDOG Logo" className="h-full w-full object-contain" />
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col rounded-md -space-y-px gap-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                autoComplete="email"
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="appearance-none relative block w-full px-3 pr-10 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.01-3.368m2.148-2.149A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.955 9.955 0 01-4.043 5.058M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 9L3 3"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.522 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S3.732 16.057 2.458 12z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Logging in...
                </div>
              ) : (
                'Login'
              )}
            </button>
          </div>

          {/* Forgot Password Link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/employee-forgot-password')}
              className="text-sm text-gray-600 hover:text-gray-500 transition-colors duration-200 hover:underline"
            >
              Forgot your password?
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Client login?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-green-600 hover:text-green-500"
              >
                Go to client portal
              </button>
            </p>
          </div>
        </form>

        {/* Password Change Modal */}
        {showPasswordModal && tempSession && (
          <ChangePasswordModal
            isOpen={showPasswordModal}
            onClose={handlePasswordChangeCancel}
            onPasswordChanged={handlePasswordChangeSuccess}
            temporaryToken={tempSession.token}
            isEmployee={true}
            userEmail={tempSession.user.email}
          />
        )}

        {/* Copyright Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">© 2026 Sports Data Partners, LLC dba STATDOG. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;
